import { useMemo, useRef, useState } from 'react'
import type { ArquivoParaEnviar } from '../api'
import type { Catalogo, DocumentoEnviado, Socio } from '../tipos'

/**
 * Etapa 5 — documentos.
 *
 * O checklist NÃO é uma lista fixa no código: sai de `precad_DocumentoRequisito`
 * cruzado com o tipo de cliente escolhido. Incluir um documento novo, ou deixar
 * de exigir CRF de hospital, é uma linha de seed — não alteração de código nem
 * republicação de action.
 *
 * Os arquivos escolhidos ficam em memória e sobem TODOS numa chamada só. A
 * Fase 0 confirmou que a plataforma aceita N arquivos por requisição, e nove
 * uploads sequenciais numa conexão de balcão seriam nove chances de falhar
 * pela metade.
 */

interface Vaga {
  chave: string
  papel: string
  nome: string
  obrigatorio: number
  socioId: number | null
  socioNome?: string
  extensoes: string
  tamanhoMaximoMb: number
}

interface Props {
  catalogo: Catalogo | null
  tipoClienteId: string
  socios: Socio[]
  enviados: DocumentoEnviado[]
  aoEnviar: (arquivos: ArquivoParaEnviar[], aoProgredir: (f: number) => void) => Promise<boolean>
  aoVoltar: () => void
  aoAvancar: () => void
  ocupado: boolean
}

export function Documentos({
  catalogo,
  tipoClienteId,
  socios,
  enviados,
  aoEnviar,
  aoVoltar,
  aoAvancar,
  ocupado,
}: Props) {
  const [escolhidos, setEscolhidos] = useState<Record<string, File>>({})
  const [erro, setErro] = useState<string | null>(null)
  const [progresso, setProgresso] = useState<number | null>(null)
  const contador = useRef(0)

  const vagas = useMemo<Vaga[]>(() => {
    const tipos = catalogo?.tiposDocumento ?? []
    const requisitos = catalogo?.requisitos ?? []
    const idTipo = Number(tipoClienteId)
    const exigidos = requisitos.filter((r) => r.tipoClienteId === idTipo)

    const daEmpresa = exigidos
      .map((r) => ({ r, t: tipos.find((t) => t.id === r.tipoDocumentoId) }))
      .filter((x) => x.t?.escopo === 'Empresa')
      .map<Vaga>(({ r, t }) => ({
        chave: `emp:${t!.codigo}`,
        papel: t!.codigo,
        nome: t!.nome,
        obrigatorio: r.obrigatorio,
        socioId: null,
        extensoes: t!.extensoesAceitas,
        tamanhoMaximoMb: t!.tamanhoMaximoMb,
      }))

    const dosSocios = socios.flatMap((s) =>
      exigidos
        .map((r) => ({ r, t: tipos.find((t) => t.id === r.tipoDocumentoId) }))
        .filter((x) => x.t?.escopo === 'Socio')
        .map<Vaga>(({ r, t }) => ({
          chave: `soc:${s.id}:${t!.codigo}`,
          papel: t!.codigo,
          nome: t!.nome,
          obrigatorio: r.obrigatorio,
          socioId: s.id ?? null,
          socioNome: s.nome,
          extensoes: t!.extensoesAceitas,
          tamanhoMaximoMb: t!.tamanhoMaximoMb,
        })),
    )

    return [...daEmpresa, ...dosSocios]
  }, [catalogo, tipoClienteId, socios])

  const jaEnviado = (v: Vaga) =>
    enviados.find((d) => d.papel === v.papel && (d.socioId ?? null) === v.socioId)

  const pendentes = vagas.filter((v) => v.obrigatorio && !jaEnviado(v) && !escolhidos[v.chave])
  const aEnviar = vagas.filter((v) => escolhidos[v.chave])

  function escolher(vaga: Vaga, arquivo: File | null) {
    setErro(null)
    if (!arquivo) {
      setEscolhidos((a) => {
        const novo = { ...a }
        delete novo[vaga.chave]
        return novo
      })
      return
    }

    const extensao = `.${arquivo.name.split('.').pop()?.toLowerCase() ?? ''}`
    if (!vaga.extensoes.split(',').includes(extensao)) {
      setErro(`"${arquivo.name}" não é um formato aceito. Envie ${vaga.extensoes}.`)
      return
    }
    if (arquivo.size > vaga.tamanhoMaximoMb * 1024 * 1024) {
      setErro(`"${arquivo.name}" passa de ${vaga.tamanhoMaximoMb} MB. Reduza o arquivo e tente de novo.`)
      return
    }

    /* Nome repetido no lote deixaria o join arquivo→papel ambíguo no servidor.
       Renomear aqui é invisível para a pessoa e resolve — a alternativa seria
       recusar um envio legítimo só porque dois documentos vieram do scanner
       com o mesmo nome padrão. */
    const jaUsado = Object.values(escolhidos).some((f) => f.name === arquivo.name)
    const arquivoFinal = jaUsado
      ? new File([arquivo], renomear(arquivo.name, ++contador.current), { type: arquivo.type })
      : arquivo

    setEscolhidos((a) => ({ ...a, [vaga.chave]: arquivoFinal }))
  }

  async function enviar() {
    if (!aEnviar.length) {
      aoAvancar()
      return
    }
    setErro(null)
    setProgresso(0)
    const ok = await aoEnviar(
      aEnviar.map((v) => ({ arquivo: escolhidos[v.chave]!, papel: v.papel, socioId: v.socioId })),
      setProgresso,
    )
    setProgresso(null)
    if (ok) setEscolhidos({})
  }

  return (
    <div>
      <p className="chamada">
        Envie os documentos abaixo. Aceitamos {vagas[0]?.extensoes ?? '.pdf'} — foto legível do
        documento também serve.
      </p>

      <Secao titulo="Documentos da empresa">
        {vagas
          .filter((v) => !v.socioId)
          .map((v) => (
            <Linha key={v.chave} vaga={v} enviado={jaEnviado(v)} escolhido={escolhidos[v.chave]} aoEscolher={escolher} />
          ))}
      </Secao>

      {socios.map((s) => {
        const doSocio = vagas.filter((v) => v.socioId === s.id)
        if (!doSocio.length) return null
        return (
          <Secao key={s.id} titulo={`Documentos de ${s.nome}`}>
            {doSocio.map((v) => (
              <Linha key={v.chave} vaga={v} enviado={jaEnviado(v)} escolhido={escolhidos[v.chave]} aoEscolher={escolher} />
            ))}
          </Secao>
        )
      })}

      {erro ? (
        <p className="aviso aviso--erro" role="alert">
          {erro}
        </p>
      ) : null}

      {progresso !== null ? (
        <p className="gravacao" role="status">
          <span className="gravacao__ponto gravacao__ponto--gravando" aria-hidden="true" />
          Enviando… {Math.round(progresso * 100)}%
        </p>
      ) : null}

      {pendentes.length ? (
        <p className="nota">
          Ainda faltam <strong>{pendentes.length}</strong> documento
          {pendentes.length > 1 ? 's' : ''} obrigatório{pendentes.length > 1 ? 's' : ''}. Você pode
          enviar o que já tem e voltar depois.
        </p>
      ) : null}

      <div className="rodape">
        <button type="button" className="botao botao--secundario" onClick={aoVoltar}>
          Voltar
        </button>
        <span className="rodape__espaco" />
        <button
          type="button"
          className="botao botao--principal"
          onClick={() => void enviar()}
          aria-disabled={ocupado}
        >
          {ocupado ? 'Enviando…' : aEnviar.length ? `Enviar ${aEnviar.length} arquivo(s)` : 'Continuar'}
        </button>
      </div>
    </div>
  )
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="secao">{titulo}</h3>
      {children}
    </section>
  )
}

function Linha({
  vaga,
  enviado,
  escolhido,
  aoEscolher,
}: {
  vaga: Vaga
  enviado?: DocumentoEnviado
  escolhido?: File
  aoEscolher: (v: Vaga, f: File | null) => void
}) {
  const estado = escolhido ? 'escolhido' : enviado ? 'enviado' : 'vazio'

  return (
    <div className={`doc doc--${estado}`}>
      <div className="doc__info">
        <span className="doc__nome">
          {vaga.nome}
          {vaga.obrigatorio ? null : <span className="campo__opcional">opcional</span>}
        </span>
        <span className="doc__estado">
          {escolhido
            ? `Pronto para enviar: ${escolhido.name}`
            : enviado
              ? `Recebido: ${enviado.nomeArquivo}`
              : `${vaga.extensoes} · até ${vaga.tamanhoMaximoMb} MB`}
        </span>
      </div>

      <label className="doc__acao">
        {enviado || escolhido ? 'Trocar' : 'Escolher arquivo'}
        <input
          type="file"
          className="oculto-visual"
          accept={vaga.extensoes}
          onChange={(e) => aoEscolher(vaga, e.target.files?.[0] ?? null)}
        />
      </label>
    </div>
  )
}

function renomear(nome: string, n: number) {
  const ponto = nome.lastIndexOf('.')
  return ponto > 0 ? `${nome.slice(0, ponto)}-${n}${nome.slice(ponto)}` : `${nome}-${n}`
}
